/**
 * Specialized Vendor Name Matcher
 * 
 * Handles vendor name matching with:
 * - Canonical name resolution
 * - Alias learning from corrections
 * - Fuzzy matching with confidence scores
 * - Company-specific learned aliases
 */

import { supabase } from '@/integrations/supabase/client';
import { 
  calculateAdvancedSimilarity, 
  normalizeForRestaurant,
  ABBREVIATION_MAP,
  soundex 
} from './advancedSimilarity';

// =============================================================================
// TYPES
// =============================================================================

export interface VendorMatchResult {
  /** Whether the vendors match */
  matched: boolean;
  /** Confidence score (0-1) */
  confidence: number;
  /** The canonical/matched vendor name */
  matchedName: string;
  /** Method used to determine match */
  method: 'exact' | 'alias' | 'fuzzy' | 'phonetic' | 'learned';
  /** Optional: the alias that was matched */
  matchedAlias?: string;
}

export interface LearnedVendorAlias {
  id: string;
  company_id: string | null;
  canonical_name: string;
  alias: string;
  source: 'manual' | 'learned' | 'default';
  created_at: string;
}

// =============================================================================
// VENDOR NAME MATCHER CLASS
// =============================================================================

export class VendorNameMatcher {
  /** Maps alias -> canonical name */
  private aliasMap: Map<string, string> = new Map();
  /** Set of all canonical names */
  private canonicalNames: Set<string> = new Set();
  /** Company-specific learned aliases */
  private learnedAliases: Map<string, string> = new Map();
  /** Company ID for learned aliases */
  private companyId: string | null = null;
  /** Whether the matcher has been initialized */
  private initialized: boolean = false;
  
  constructor() {
    this.buildDefaultAliasMap();
  }
  
  /**
   * Build alias map from default abbreviations
   */
  private buildDefaultAliasMap(): void {
    for (const [canonical, variations] of Object.entries(ABBREVIATION_MAP)) {
      const normalizedCanonical = canonical.toLowerCase().trim();
      this.canonicalNames.add(normalizedCanonical);
      this.aliasMap.set(normalizedCanonical, normalizedCanonical);
      
      for (const variation of variations) {
        const normalizedVariation = variation.toLowerCase().trim();
        this.aliasMap.set(normalizedVariation, normalizedCanonical);
      }
    }
  }
  
  /**
   * Initialize with company-specific learned aliases
   */
  async initialize(companyId: string): Promise<void> {
    this.companyId = companyId;
    
    try {
      // Load learned aliases for this company
      const { data: aliases, error } = await supabase
        .from('vendor_aliases')
        .select('*')
        .or(`company_id.eq.${companyId},company_id.is.null`)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error loading vendor aliases:', error);
        return;
      }
      
      if (aliases) {
        for (const alias of aliases) {
          const normalizedAlias = alias.alias.toLowerCase().trim();
          const normalizedCanonical = alias.canonical_name.toLowerCase().trim();
          
          // Company-specific aliases take precedence
          if (alias.company_id === companyId) {
            this.learnedAliases.set(normalizedAlias, normalizedCanonical);
          } else if (!this.aliasMap.has(normalizedAlias)) {
            // Global aliases only if not already defined
            this.aliasMap.set(normalizedAlias, normalizedCanonical);
          }
          
          this.canonicalNames.add(normalizedCanonical);
        }
      }
      
      this.initialized = true;
    } catch (error) {
      console.error('Error initializing VendorNameMatcher:', error);
    }
  }
  
  /**
   * Get canonical vendor name from any variation
   */
  getCanonicalName(vendorName: string): string {
    const normalized = vendorName.toLowerCase().trim();
    
    // Check learned aliases first (company-specific)
    if (this.learnedAliases.has(normalized)) {
      return this.learnedAliases.get(normalized)!;
    }
    
    // Check default aliases
    if (this.aliasMap.has(normalized)) {
      return this.aliasMap.get(normalized)!;
    }
    
    // Try to find partial match in canonical names
    for (const canonical of this.canonicalNames) {
      if (normalized.includes(canonical) || canonical.includes(normalized)) {
        return canonical;
      }
    }
    
    // Return normalized input as-is
    return normalized;
  }
  
  /**
   * Match two vendor names with confidence
   */
  matchVendors(vendor1: string, vendor2: string): VendorMatchResult {
    if (!vendor1 || !vendor2) {
      return {
        matched: false,
        confidence: 0,
        matchedName: '',
        method: 'exact'
      };
    }
    
    const norm1 = vendor1.toLowerCase().trim();
    const norm2 = vendor2.toLowerCase().trim();
    
    // Exact match
    if (norm1 === norm2) {
      return {
        matched: true,
        confidence: 1.0,
        matchedName: norm1,
        method: 'exact'
      };
    }
    
    // Get canonical names
    const canonical1 = this.getCanonicalName(vendor1);
    const canonical2 = this.getCanonicalName(vendor2);
    
    // Canonical match (via alias)
    if (canonical1 === canonical2) {
      return {
        matched: true,
        confidence: 0.95,
        matchedName: canonical1,
        method: norm1 === canonical1 || norm2 === canonical2 ? 'alias' : 'learned'
      };
    }
    
    // Check learned aliases
    if (this.learnedAliases.has(norm1) && this.learnedAliases.get(norm1) === canonical2) {
      return {
        matched: true,
        confidence: 0.92,
        matchedName: canonical2,
        method: 'learned',
        matchedAlias: norm1
      };
    }
    
    if (this.learnedAliases.has(norm2) && this.learnedAliases.get(norm2) === canonical1) {
      return {
        matched: true,
        confidence: 0.92,
        matchedName: canonical1,
        method: 'learned',
        matchedAlias: norm2
      };
    }
    
    // Phonetic match
    const soundex1 = soundex(canonical1);
    const soundex2 = soundex(canonical2);
    if (soundex1 && soundex2 && soundex1 === soundex2) {
      return {
        matched: true,
        confidence: 0.80,
        matchedName: canonical1,
        method: 'phonetic'
      };
    }
    
    // Fuzzy match using advanced similarity
    const fuzzyResult = calculateAdvancedSimilarity(canonical1, canonical2, {
      domainSpecific: true
    });
    
    if (fuzzyResult.score >= 0.85) {
      return {
        matched: true,
        confidence: fuzzyResult.score,
        matchedName: canonical1.length >= canonical2.length ? canonical1 : canonical2,
        method: 'fuzzy'
      };
    }
    
    // No match
    return {
      matched: false,
      confidence: fuzzyResult.score,
      matchedName: '',
      method: 'fuzzy'
    };
  }
  
  /**
   * Add a learned alias (from user correction)
   */
  async addLearnedAlias(
    canonicalName: string, 
    alias: string,
    source: 'manual' | 'learned' = 'learned'
  ): Promise<boolean> {
    if (!this.companyId) {
      console.warn('VendorNameMatcher not initialized with companyId');
      return false;
    }
    
    const normalizedCanonical = canonicalName.toLowerCase().trim();
    const normalizedAlias = alias.toLowerCase().trim();
    
    // Skip if alias is same as canonical
    if (normalizedCanonical === normalizedAlias) {
      return false;
    }
    
    // Skip if already exists
    if (this.learnedAliases.get(normalizedAlias) === normalizedCanonical) {
      return false;
    }
    
    try {
      // Upsert to database
      const { error } = await supabase
        .from('vendor_aliases')
        .upsert({
          company_id: this.companyId,
          canonical_name: normalizedCanonical,
          alias: normalizedAlias,
          source
        }, {
          onConflict: 'company_id,alias'
        });
      
      if (error) {
        console.error('Error saving vendor alias:', error);
        return false;
      }
      
      // Update local cache
      this.learnedAliases.set(normalizedAlias, normalizedCanonical);
      this.canonicalNames.add(normalizedCanonical);
      
      return true;
    } catch (error) {
      console.error('Error adding vendor alias:', error);
      return false;
    }
  }
  
  /**
   * Find all possible aliases for a vendor name
   */
  getAliases(vendorName: string): string[] {
    const canonical = this.getCanonicalName(vendorName);
    const aliases: string[] = [];
    
    // Find all aliases pointing to this canonical name
    for (const [alias, can] of this.aliasMap) {
      if (can === canonical && alias !== canonical) {
        aliases.push(alias);
      }
    }
    
    for (const [alias, can] of this.learnedAliases) {
      if (can === canonical && alias !== canonical && !aliases.includes(alias)) {
        aliases.push(alias);
      }
    }
    
    return aliases;
  }
  
  /**
   * Check if a vendor is known (in canonical names or aliases)
   */
  isKnownVendor(vendorName: string): boolean {
    const normalized = vendorName.toLowerCase().trim();
    return this.canonicalNames.has(normalized) || 
           this.aliasMap.has(normalized) || 
           this.learnedAliases.has(normalized);
  }
  
  /**
   * Get statistics about the matcher
   */
  getStats(): {
    canonicalCount: number;
    defaultAliasCount: number;
    learnedAliasCount: number;
    initialized: boolean;
  } {
    return {
      canonicalCount: this.canonicalNames.size,
      defaultAliasCount: this.aliasMap.size,
      learnedAliasCount: this.learnedAliases.size,
      initialized: this.initialized
    };
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

/**
 * Singleton vendor name matcher for global use
 */
export const vendorNameMatcher = new VendorNameMatcher();

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Extract vendor name from bank transaction description
 */
export function extractVendorFromDescription(description: string): string | null {
  if (!description) return null;
  
  // Normalize first
  const normalized = normalizeForRestaurant(description);
  if (!normalized) return null;
  
  // Common patterns for vendor names in bank descriptions
  const patterns = [
    // "ORIG CO NAME: Vendor Name" pattern (ACH)
    /orig\s+co\s+name[:\s]+([^,\n]+)/i,
    // "COMPANY: Vendor Name" pattern
    /company[:\s]+([^,\n]+)/i,
    // "MERCHANT: Vendor Name" pattern
    /merchant[:\s]+([^,\n]+)/i,
    // "PAYEE: Vendor Name" pattern
    /payee[:\s]+([^,\n]+)/i,
  ];
  
  for (const pattern of patterns) {
    const match = description.match(pattern);
    if (match && match[1]) {
      const extracted = match[1].trim().toLowerCase();
      if (extracted.length > 2) {
        return extracted;
      }
    }
  }
  
  // Try to extract capitalized company name at start
  const capsMatch = description.match(/^([A-Z][A-Za-z\s&]+?)(?:\s+INC|\s+LLC|\s+LTD|\s+CORP|,|\s+#|\s+\d)/);
  if (capsMatch && capsMatch[1] && capsMatch[1].trim().length > 2) {
    return capsMatch[1].trim().toLowerCase();
  }
  
  // Fall back to first few significant words
  const words = normalized.split(/\s+/).filter(w => w.length > 2);
  if (words.length >= 1) {
    // Return first 2-3 words as vendor name
    return words.slice(0, Math.min(3, words.length)).join(' ');
  }
  
  return null;
}

/**
 * Find best matching vendor from a list
 */
export function findBestVendorMatch(
  query: string,
  vendorList: string[],
  minConfidence: number = 0.7
): VendorMatchResult | null {
  if (!query || vendorList.length === 0) return null;
  
  let bestMatch: VendorMatchResult | null = null;
  
  for (const vendor of vendorList) {
    const result = vendorNameMatcher.matchVendors(query, vendor);
    
    if (result.matched && result.confidence >= minConfidence) {
      if (!bestMatch || result.confidence > bestMatch.confidence) {
        bestMatch = {
          ...result,
          matchedName: vendor // Use original vendor name from list
        };
      }
    }
  }
  
  return bestMatch;
}
